import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { PrimaryButton } from "../../buttons/primaryButton/PrimaryButton";

export interface IAffiliation {
  title?: string;
  image: string;
  button_text: string;
  button_url: string;
}

export interface IAffiliationModalProps {
  visible: boolean;
  data: IAffiliation;
  onClose?: () => void;
}

export const AffiliationModal: React.FC<IAffiliationModalProps> = ({
  data,
  visible,
  onClose,
}) => {
  const [canClose, setCanClose] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(5);

  useEffect(() => {
    if (!visible) return;

    setCanClose(false);
    setSecondsRemaining(5);

    const interval = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanClose(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [visible]);

  // Keyboard close support (ESC)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && canClose) {
        onClose?.();
      }
    };
    if (visible) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [visible, canClose, onClose]);

  const onLinkPress = useCallback(() => {
    if (data?.button_url) {
      window.open(data.button_url, "_blank", "noopener,noreferrer");
      onClose?.();
    }
  }, [data?.button_url, onClose]);

  const radius = 15;
  const circumference = 2 * Math.PI * radius;

  return (
    <AnimatePresence>
      {visible && data && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-md select-none"
          onClick={() => {
            if (canClose) onClose?.();
          }}
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 15 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 15 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="relative w-full max-w-[420px] max-h-[90vh] flex flex-col rounded-3xl shadow-2xl bg-[#040822] ring-1 ring-white/15 overflow-visible"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Circular Countdown Close Button placed outside top-right to never overlap banner text */}
            <button
              type="button"
              onClick={canClose ? onClose : undefined}
              disabled={!canClose}
              style={{ opacity: canClose ? 1 : 0.8 }}
              className={`absolute -top-3 -right-3 sm:-top-4 sm:-right-4 z-30 w-11 h-11 rounded-full bg-[#141838] border border-white/20 shadow-xl flex items-center justify-center transition-all ${
                canClose
                  ? "cursor-pointer hover:bg-[#1f2554] hover:scale-105 active:scale-95 text-white"
                  : "cursor-not-allowed text-white/70"
              }`}
              title={
                canClose
                  ? "Close (Esc)"
                  : `Please wait ${secondsRemaining}s to close`
              }
            >
              <svg className="w-10 h-10 -rotate-90 absolute">
                <circle
                  cx="20"
                  cy="20"
                  r={radius}
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.2)"
                  strokeWidth="2.5"
                />
                <motion.circle
                  key={visible ? "open" : "closed"}
                  cx="20"
                  cy="20"
                  r={radius}
                  fill="none"
                  stroke="#020637"
                  strokeWidth="2.5"
                  strokeDasharray={circumference}
                  initial={{ strokeDashoffset: circumference }}
                  animate={{ strokeDashoffset: 0 }}
                  transition={{ duration: 5, ease: "linear" }}
                  strokeLinecap="round"
                />
              </svg>
              <span className="relative z-10 flex items-center justify-center">
                {canClose ? (
                  <X size={18} strokeWidth={2.5} className="text-white" />
                ) : (
                  <span className="text-[11px] font-semibold text-white/90 font-mono">
                    {secondsRemaining}s
                  </span>
                )}
              </span>
            </button>

            {/* Inner rounded container for image and action button */}
            <div className="relative w-full min-h-0 flex-1 rounded-3xl overflow-hidden flex flex-col">
              {/* Full Image - natural aspect ratio, no cropping; shrinks so the
                  action button below always stays within the viewport */}
              <div className="relative w-full flex-1 min-h-0 flex items-center justify-center bg-[#040822]">
                <img
                  src={data.image}
                  alt={data.title || "Affiliation"}
                  className="w-full h-auto max-h-full object-contain block select-none pointer-events-none"
                />

                {/* Bottom gradient overlay to blend into button area */}
                <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#040822] via-[#040822]/80 to-transparent pointer-events-none" />
              </div>

              {/* Action Button matching web version design */}
              <div className="relative z-10 shrink-0 px-6 pb-6 pt-2 bg-[#040822] w-full">
                <PrimaryButton
                  onClick={onLinkPress}
                  label={data.button_text}
                  size="large"
                  className="w-full justify-center !py-3.5 !rounded-2xl text-base font-semibold bg-gradient-to-r from-[#020637] to-[#1e3aa0] hover:from-[#0e1c7a] hover:to-[#1e3aa0] text-white shadow-lg shadow-[#020637]/30 transition-all active:scale-[0.98]"
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
